'use client';

import { 
  ControlType, 
  CalculatorId, 
  CalculatorType, 
  ControlIds, 
  type CalculatorConfig, 
  type ControlValues,
  type CalculatorControl,
  type SingleColumnSelectControl,
  type TextControl,
  type NumberControl
} from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { LazyTabLabECharts } from '@/components/ui/charts/lazy-tab-lab-echarts';
import type { EChartsOption } from 'echarts';

// Type for histogram specific control values
export interface HistogramControlValues extends ControlValues {
  [ControlIds.DataColumn]: string;
  [ControlIds.Title]: string;
  [ControlIds.BinCount]: number;
}

// Define the controls for the histogram
export const histogramControls = {
  tabs: [
    {
      title: 'Data',
      controls: [
        {
          id: ControlIds.DataColumn,
          type: ControlType.SingleColumnSelect,
          label: 'Data column',
          required: true,
          numericOnly: true
        } satisfies SingleColumnSelectControl,
        {
          id: ControlIds.Title,
          type: ControlType.Text,
          label: 'Chart title'
        } satisfies TextControl
      ] as readonly CalculatorControl[]
    },
    {
      title: 'Appearance',
      controls: [
        {
          id: ControlIds.BinCount,
          type: ControlType.Number,
          label: 'Number of bins',
          min: 5,
          max: 50,
          step: 1,
          defaultValue: 10
        } satisfies NumberControl
      ] as readonly CalculatorControl[]
    }
  ]
};

function calculateHistogramData(data: number[], binCount: number): { 
  bins: [number, number][]; // [binStart, frequency]
  binEdges: number[];
} {
  const min = Math.min(...data);
  const max = Math.max(...data);
  const binWidth = (max - min) / binCount;
  
  // Create bin edges
  const binEdges = Array.from({ length: binCount + 1 }, (_, i) => min + i * binWidth);
  
  // Initialize bins with 0 frequency
  const bins = Array.from({ length: binCount }, (_, i) => [
    binEdges[i] ?? min,
    0
  ] as [number, number]);
  
  // Count frequencies
  data.forEach(value => {
    // Special case for the maximum value - put it in the last bin
    if (value === max) {
      const lastBin = bins[bins.length - 1];
      if (lastBin) lastBin[1]++;
      return;
    }
    
    const binIndex = Math.floor((value - min) / binWidth);
    if (binIndex >= 0 && binIndex < binCount) {
      const bin = bins[binIndex];
      if (bin) bin[1]++;
    }
  });
  
  return { bins, binEdges };
}

// Function to get histogram configuration
function getHistogramConfig({
  data,
  title,
  binCount
}: {
  data: number[];
  title: string;
  binCount: number;
}): EChartsOption {
  const { bins, binEdges } = calculateHistogramData(data, binCount);
  
  return {
    title: {
      text: title,
      left: 'center'
    },
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        const binStart = params.data[0];
        const binEnd = binEdges[params.dataIndex + 1] ?? binStart;
        return `Range: ${binStart.toFixed(2)} - ${binEnd.toFixed(2)}<br/>Frequency: ${params.data[1]}`;
      }
    },
    toolbox: {
      show: true,
      feature: {
        saveAsImage: {
          title: 'Save as Image',
          name: 'histogram'
        },
        dataZoom: {
          title: {
            zoom: 'Zoom',
            back: 'Zoom Reset'
          }
        }
      }
    },
    grid: {
      top: 60,
      right: 40,
      bottom: 60,
      left: 60
    },
    xAxis: {
      type: 'value',
      scale: true
    },
    yAxis: {
      type: 'value'
    },
    series: [{
      type: 'bar',
      data: bins,
      barWidth: '100%',
      barGap: '0%',
      emphasis: {
        focus: 'series',
        itemStyle: {
          shadowBlur: 10,
          shadowColor: 'rgba(0, 0, 0, 0.5)'
        }
      }
    }]
  };
}

export const histogramConfig: CalculatorConfig<CalculatorId.Histogram> = {
  id: CalculatorId.Histogram,
  type: CalculatorType.Graph,
  name: 'Histogram',
  initialData: TableDataJSON.from({
    columns: [
      { 
        id: 'height', 
        name: 'Height (cm)', 
        // Normal distribution with mean 170, std 10
        values: [
          '158', '162', '165', '166', '167', '168', '169', '170', '170', '171',
          '171', '172', '172', '173', '173', '174', '174', '175', '176', '178',
          '165', '167', '169', '170', '171', '172', '173', '174', '175', '177'
        ]
      }
    ]
  }),
  initialControlValues: {
    [ControlIds.DataColumn]: 'height',
    [ControlIds.Title]: 'Height Distribution',
    [ControlIds.BinCount]: 10
  },
  controls: histogramControls,
  getInitialConfig: (data: TableDataJSON) => {
    const numericColumns = data.columns.filter(col => 
      col.values.every(val => !isNaN(Number(val)))
    );

    const firstColumn = numericColumns[0];

    return {
      [ControlIds.DataColumn]: firstColumn?.id ?? '',
      [ControlIds.Title]: firstColumn ? `${firstColumn.name} Distribution` : 'Histogram',
      [ControlIds.BinCount]: 10
    };
  },
  getVisualizationData: ({ data, controls }) => {
    const histogramControls = controls as HistogramControlValues;
    
    if (!data?.columns?.length || !histogramControls[ControlIds.DataColumn]) {
      console.log('Missing data or controls:', { data, controls });
      return null;
    }

    const dataColumn = data.columns.find(col => col.id === histogramControls[ControlIds.DataColumn]);

    if (!dataColumn) {
      console.log('Data column not found:', histogramControls[ControlIds.DataColumn]);
      return null;
    }

    // Convert string values to numbers
    const numericData = dataColumn.values.map(val => Number(val) || 0);

    return getHistogramConfig({
      data: numericData,
      title: histogramControls[ControlIds.Title],
      binCount: histogramControls[ControlIds.BinCount]
    });
  },
  render: ({ visualizationData }) => 
    visualizationData ? (
      <LazyTabLabECharts option={visualizationData} className="h-full w-full" />
    ) : null
}; 